/**
 * Secondary stream + IB-track spoke content dispatcher.
 *
 * All 20 Sec 1-4 x G1/G2/G3/IP/IB routes resolve to the single
 * `SecondaryStreamPage` component, so this whole cluster is one lazy chunk. The
 * per-stream data + builders live in `secondaryStream{G1,G2,G3,IP,Ib}` (kept as
 * separate files so no single module is thousands of lines); this module just
 * routes a (level, stream) pair to the right builder.
 */
import { buildSecondaryG1Content } from './secondaryStreamG1';
import { buildSecondaryG2Content } from './secondaryStreamG2';
import { buildSecondaryG3Content } from './secondaryStreamG3';
import { buildSecondaryIbContent } from './secondaryStreamIb';
import { buildSecondaryIpContent } from './secondaryStreamIP';
import type { LevelPageContent } from './types';

const STREAM_BUILDERS: Record<
  string,
  (secLevel: number, brandName: string) => LevelPageContent
> = {
  g1: buildSecondaryG1Content,
  g2: buildSecondaryG2Content,
  g3: buildSecondaryG3Content,
  ip: buildSecondaryIpContent,
  ib: buildSecondaryIbContent,
};

/**
 * Resolve one Sec 1-4 x G1/G2/G3/IP/IB stream page's content (with its
 * stream-specific FAQ + decision-support data already attached by the per-stream
 * builder). Returns undefined for an unrecognised stream so callers can redirect.
 */
export function buildSecondaryStreamContent(
  secLevel: string | number,
  stream: string,
  brandName = 'DeepThink'
): LevelPageContent | undefined {
  const builder = STREAM_BUILDERS[stream.toLowerCase()];
  return builder ? builder(Number(secLevel), brandName) : undefined;
}
